import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { C, Avatar, Btn } from "./UI";

export default function Navbar() {
  const { user, userType, profile, logout } = useApp();

  // Obter o primeiro nome do usuario
  const getFirstName = () => {
    // Prioridade: nome do profile > nome do user > parte do email antes do @
    if (profile?.name) {
      return profile.name.split(" ")[0];
    }
    if (user?.user_metadata?.name) {
      return user.user_metadata.name.split(" ")[0];
    }
    if (user?.email) {
      return user.email.split("@")[0];
    }
    return "Usuario";
  };

  // Gerar iniciais do nome do usuario
  const getInitials = () => {
    const name = profile?.name || user?.name || user?.email || "";
    if (!name) return "U";
    const parts = name.trim().split(" ").filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fecha menu ao navegar
  useEffect(() => {
    setMenuOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname]);

  const isDash = location.pathname.startsWith("/dashboard");

  const navLinks = [
    { label: "Como funciona", path: "/#como-funciona" },
    { label: "Sobre", path: "/sobre" },
    { label: "Para Elas", path: "/para-elas" },
    { label: "Para Empresas", path: "/empresas" },
    { label: "Dúvidas", path: "/duvidas" },
    { label: "Contato", path: "/contato" },
  ];

  return (
    <>
      <nav style={{
        position: "sticky", top: 0, zIndex: 100,
        background: "rgba(250,249,247,0.95)", backdropFilter: "blur(12px)",
        borderBottom: `0.5px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "0 16px" : "0 40px", 
        height: 60,
      }}>
        {/* Logo */}
        <div onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
          <img 
            src="/images/logo-womom.png" 
            alt="WoMom" 
            style={{ 
              height: isMobile ? 100 : 160,
              width: "auto",
              objectFit: "contain"
            }} 
          />
        </div>

        {/* Links Desktop */}
        {!isDash && !isMobile && !isTablet && (
          <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
            {navLinks.map(l => (
              <span key={l.label} onClick={() => navigate(l.path)}
                style={{ fontSize: 14, color: C.textSec, cursor: "pointer", transition: "color 0.15s" }}
                onMouseEnter={e => e.target.style.color = C.roseMid}
                onMouseLeave={e => e.target.style.color = C.textSec}
              >{l.label}</span>
            ))}
          </div>
        )}

        {/* Right side */}
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {/* Auth area - Desktop */}
          {!isMobile && !isTablet && (
            <>
              {user ? (
                <div style={{ position: "relative" }}>
                  <div 
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    style={{ cursor: "pointer" }}
                  >
                    <Avatar initials={getInitials()} size={34} />
                  </div>
                  
                  {/* Dropdown Menu */}
                  {profileMenuOpen && (
                    <>
                      <div 
                        onClick={() => setProfileMenuOpen(false)}
                        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                      />
                      <div style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        marginTop: 8,
                        background: C.surface,
                        borderRadius: 12,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                        border: `1px solid ${C.border}`,
                        minWidth: 200,
                        padding: "8px 0",
                        zIndex: 100
                      }}>
                        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: C.textMain }}>{getFirstName()}</div>
                          <div style={{ fontSize: 12, color: C.textSec }}>{user.email || ""}</div>
                        </div>
                        
                        {[
                          { label: "Meu Perfil", path: "/meu-perfil", icon: "👤" },
                          { label: "Meu Currículo", path: "/completar-curriculo", icon: "📄" },
                          { label: "Minhas Avaliações", path: "/minhas-avaliacoes", icon: "⭐" },
                          { label: "Vagas Salvas", path: "/vagas-salvas", icon: "💼" },
                          { label: "Configurações", path: "/configuracoes", icon: "⚙️" },
                          ...(profile?.is_admin ? [{ label: "Painel Admin", path: "/admin", icon: "🔐" }] : []),
                        ].map(item => (
                          <div
                            key={item.label}
                            onClick={() => { navigate(item.path); setProfileMenuOpen(false); }}
                            style={{
                              display: "flex", alignItems: "center", gap: 10,
                              padding: "10px 16px", fontSize: 14, color: C.textSec,
                              cursor: "pointer", transition: "background 0.15s"
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = C.bg}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <span style={{ fontSize: 16 }}>{item.icon}</span>
                            {item.label}
                          </div>
                        ))}
                        
                        <div style={{ height: 1, background: C.border, margin: "4px 0" }} />
                        
                        <div
                          onClick={() => { logout(); setProfileMenuOpen(false); }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 16px", fontSize: 14, color: "#DC2626",
                            cursor: "pointer", transition: "background 0.15s"
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "#FEE2E2"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <span style={{ fontSize: 16 }}>🚪</span>
                          Sair
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Btn variant="ghost" onClick={() => navigate("/login")} style={{ fontSize: 13 }}>Entrar</Btn>
                  <Btn variant="primary" onClick={() => navigate("/cadastro")} style={{ padding: "9px 20px", fontSize: 13 }}>Criar conta grátis</Btn>
                </>
              )}
            </>
          )}

          {/* Hamburger menu button - Mobile/Tablet */}
          {(isMobile || isTablet) && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: "transparent",
                border: "none",
                padding: 8,
                display: "flex",
                flexDirection: "column",
                gap: 5,
                cursor: "pointer"
              }}
            >
              <span style={{ 
                width: 24, height: 2, background: C.textMain, borderRadius: 2,
                transition: "transform 0.3s, opacity 0.3s",
                transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none"
              }} />
              <span style={{ 
                width: 24, height: 2, background: C.textMain, borderRadius: 2,
                transition: "opacity 0.3s",
                opacity: menuOpen ? 0 : 1
              }} />
              <span style={{ 
                width: 24, height: 2, background: C.textMain, borderRadius: 2,
                transition: "transform 0.3s",
                transform: menuOpen ? "rotate(-45deg) translateY(-7px)" : "none"
              }} />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile/Tablet Menu Overlay */}
      {(isMobile || isTablet) && menuOpen && (
        <>
          <div 
            onClick={() => setMenuOpen(false)}
            style={{
              position: "fixed",
              top: 60,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.3)",
              zIndex: 98
            }}
          />
          <div style={{
            position: "fixed",
            top: 60,
            left: 0,
            right: 0,
            background: C.surface,
            zIndex: 99,
            padding: "20px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            maxHeight: "calc(100vh - 60px)",
            overflowY: "auto"
          }}>
            {/* User info if logged in */}
            {user && (
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 12, 
                padding: "16px",
                background: C.rosePale,
                borderRadius: 12,
                marginBottom: 16
              }}>
                <Avatar initials={getInitials()} size={40} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.textMain }}>{getFirstName()}</div>
                  <div style={{ fontSize: 13, color: C.textSec }}>{user.email || ""}</div>
                </div>
              </div>
            )}

            {/* Navigation Links */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {navLinks.map(l => (
                <div
                  key={l.label}
                  onClick={() => { navigate(l.path); setMenuOpen(false); }}
                  style={{
                    padding: "14px 16px",
                    fontSize: 15,
                    color: C.textMain,
                    cursor: "pointer",
                    borderRadius: 8,
                    transition: "background 0.15s"
                  }}
                >
                  {l.label}
                </div>
              ))}
            </div>

            {/* User menu items if logged in */}
            {user && (
              <>
                <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {[
                    { label: "Meu Perfil", path: "/meu-perfil", icon: "👤" },
                    { label: "Meu Currículo", path: "/completar-curriculo", icon: "📄" },
                    { label: "Minhas Avaliações", path: "/minhas-avaliacoes", icon: "⭐" },
                    { label: "Vagas Salvas", path: "/vagas-salvas", icon: "💼" },
                    { label: "Configurações", path: "/configuracoes", icon: "⚙️" },
                  ].map(item => (
                    <div
                      key={item.label}
                      onClick={() => { navigate(item.path); setMenuOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "14px 16px", fontSize: 15, color: C.textSec,
                        cursor: "pointer", borderRadius: 8
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{item.icon}</span>
                      {item.label}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Auth buttons */}
            <div style={{ height: 1, background: C.border, margin: "16px 0" }} />
            {user ? (
              <Btn 
                variant="danger" 
                full 
                onClick={() => { logout(); setMenuOpen(false); }}
                style={{ padding: "14px 24px" }}
              >
                Sair da conta
              </Btn>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Btn variant="primary" full onClick={() => { navigate("/login"); setMenuOpen(false); }} style={{ padding: "14px 24px" }}>
                  Entrar
                </Btn>
                <Btn variant="secondary" full onClick={() => { navigate("/cadastro"); setMenuOpen(false); }} style={{ padding: "14px 24px" }}>
                  Criar conta grátis
                </Btn>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
